import { MedplumClient } from "@medplum/core";

import { EncounterServicesStatusCode } from "@/billing/constants/encounter-services-status";
import {
  EncounterServicesStatusCodeValue,
  ServicesStatusTaggedResult,
  SetEncounterServicesStatusResult,
} from "@/billing/types/encounter-services-status";
import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";

/**
 * Writes one `qh-encounter-services-status` code onto an Encounter's `meta.tag`.
 *
 * Every code from this system goes on through here, because the states are exclusive: any code
 * already there from this system is dropped and replaced, so an Encounter never carries two at once
 * and the Missing Services queue can read a row's state off the one tag it arrived with. Tags from
 * every other system are kept as they were.
 *
 * The Encounter is read here rather than taken as a parameter. A caller that read it earlier in its
 * own flow has a copy from before its own writes went out, and an update built on that copy would put
 * back whatever changed in between. The read bypasses the request cache for the same reason: a cached
 * copy can be up to a minute old, and the update writes the whole resource back.
 *
 * @param code The state the Encounter has reached — see {@link EncounterServicesStatusCode}.
 */
export async function setEncounterServicesStatus(
  medplum: MedplumClient,
  encounterId: string,
  code: EncounterServicesStatusCodeValue,
): Promise<SetEncounterServicesStatusResult> {
  try {
    const encounter = await medplum.readResource("Encounter", encounterId, { cache: "no-cache" });
    const otherTags = (encounter.meta?.tag ?? []).filter(
      (tag) => tag.system !== QuiltedHealthCodeSystems.EncounterServicesStatus,
    );

    await medplum.updateResource({
      ...encounter,
      meta: {
        ...encounter.meta,
        tag: [...otherTags, { system: QuiltedHealthCodeSystems.EncounterServicesStatus, code }],
      },
    });

    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      reason: "write_failed",
      details: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Tags the Encounter after a billing write that already committed, and folds the tag's outcome into
 * that write's own success result.
 *
 * Every write that moves an Encounter's Services along works the same way: it writes the ChargeItems
 * first and tags the Encounter second. The charges are the record billing reads, and the tag is only
 * what the Missing Services queue reads, so a failed tag undoes nothing. It comes back as
 * `servicesStatusTagFailure` on a result that is still `ok`, and the caller reports the write as done
 * while telling the user the queue label is behind.
 *
 * The tag is not written inside the charges' own transaction, because guarding an Encounter update
 * with its `versionId` would let a stale version take the charges down with it, and leaving it
 * unguarded would put back whatever else wrote to the Encounter in the meantime.
 *
 * @param code The state the Encounter has reached now that the write is committed.
 * @param data What the write produced, returned unchanged either way.
 */
export async function tagServicesStatusAfterWrite<Data>(
  medplum: MedplumClient,
  encounterId: string,
  code: EncounterServicesStatusCodeValue,
  data: Data,
): Promise<ServicesStatusTaggedResult<Data>> {
  const servicesStatus = await setEncounterServicesStatus(medplum, encounterId, code);

  if (servicesStatus.ok) return { ok: true, data };

  return {
    ok: true,
    data,
    servicesStatusTagFailure: servicesStatus.details ?? servicesStatus.reason,
  };
}
